// Depth map files: CZDepth EXRs from 3ds Max in, the PNG the tour reads out, and matching files to renders.

/** Depth file name to its render's name: drops the extension and a depth suffix (Pano_01_depth.exr, Pano_01.CZDepth.exr). */
export function depthName(file: string): string {
  return file.replace(/^.*[\\/]/, '').replace(/\.exr$/i, '').replace(/[._ -]*(cz)?(z)?depth\d*$/i, '').trim().toLowerCase();
}

/** Metres (rows bottom first) to the depth PNG the tour reads: millimetres in RGB, top row first, white = nothing measured. */
export async function depthPng(data: Float32Array, w: number, h: number): Promise<Blob> {
  const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  const g = cv.getContext('2d')!;
  const img = g.createImageData(w, h), px = img.data;
  for (let y = 0; y < h; y++) {
    const src = (h - 1 - y) * w;
    for (let x = 0; x < w; x++) {
      const d = data[src + x];
      const mm = !(d > 0) || d >= 149 ? 0xffffff : Math.min(0xfffffe, Math.max(1, Math.round(d * 1000)));
      const k = (y * w + x) * 4;
      px[k] = mm >> 16; px[k + 1] = (mm >> 8) & 255; px[k + 2] = mm & 255; px[k + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  return new Promise((res, rej) => cv.toBlob((b) => (b ? res(b) : rej(new Error('Couldn’t write the depth map'))), 'image/png'));
}

/** Where 3ds Max's camera transform (from an EXR) puts the camera, in metres, Y up, for a guess at the scene units. */
export function cameraPoint(m: number[], unit: number): [number, number, number] {
  return [m[12] * unit, m[14] * unit, -m[13] * unit];
}

/** Scene units 3ds Max scenes are usually in, metres each. */
export const SCENE_UNITS = [0.001, 0.01, 0.0254, 1, 0.1, 0.3048];

/**
 * Matches EXR files to renders: by name first (Pano_01.exr, Pano_01_depth.exr), then by the camera position
 * 3ds Max writes into each EXR, in whichever scene units put most of them on a render.
 */
export function matchDepth<T extends { name: string; x: number; y: number; z: number }>(files: Array<{ name: string; cam: number[] | null }>, targets: T[]): Array<T | null> {
  const byName = new Map(targets.map((p) => [p.name.toLowerCase(), p]));
  const out = files.map((f) => byName.get(depthName(f.name)) ?? null);
  const unmatched = files.map((f, i) => (out[i] ? -1 : i)).filter((i) => i >= 0 && files[i].cam);
  if (!unmatched.length) return out;
  for (const unit of SCENE_UNITS) {
    const hits = unmatched.map((i) => nearestTo(cameraPoint(files[i].cam!, unit), targets, 0.05));
    if (hits.filter(Boolean).length >= Math.ceil(unmatched.length * 0.8)) { unmatched.forEach((i, k) => { out[i] = hits[k]; }); break; }
  }
  return out;
}

/** The target within `within` metres of a point, if there is one. */
export function nearestTo<T extends { x: number; y: number; z: number }>(pt: [number, number, number], targets: T[], within: number): T | null {
  let best: T | null = null, bd = Infinity;
  for (const p of targets) { const d = Math.hypot(p.x - pt[0], p.y - pt[1], p.z - pt[2]); if (d < bd) { bd = d; best = p; } }
  return bd < within ? best : null;
}
